import React, { useState, useEffect, useRef } from 'react';
import '../../styles/onboardingFlow/onboarding/onboardingSidebar.scss';
import SidebarCard from '../onboardingComponents/SidebarCard';
import adupLogoSet from '../../assets/onboarding/common/adup-logo-set.png';
import window1Card1Back from '../../assets/onboarding/sidebar/card1/window1.svg';
import window1Card1Front from '../../assets/onboarding/sidebar/card1/window2.svg';

interface OnboardingSidebarProps {
  currentStep: number;
  onStepChange: (stepNumber: number) => void;
  isStepCompleted: (stepNumber: number) => boolean;
  isStepAccessible: (stepNumber: number) => boolean;
}

// Streamlined onboarding is a single screen (choose your account type), so the
// sidebar shows one step card and one promo card. The old multi-step promo
// carousel (cards 2–5) was removed along with the steps it advertised.
const promoCard = {
  title: 'Automated reporting',
  subtitle: 'Smart, Unified, Instant',
  description:
    'Connect once and let TARA handle the rest. Get clear, automated reports across every channel - always up-to-date, always ready to share.',
};

const OnboardingSidebar: React.FC<OnboardingSidebarProps> = ({
  currentStep,
  onStepChange,
  isStepCompleted,
  isStepAccessible,
}) => {
  const [, setLoadedImages] = useState<Set<string>>(new Set());
  const imageLoadCountRef = useRef(0);

  const handleImageLoad = (imageKey: string) => {
    setLoadedImages((prev) => {
      const newSet = new Set(prev);
      newSet.add(imageKey);
      return newSet;
    });
    imageLoadCountRef.current += 1;
  };

  useEffect(() => {
    const checkImageLoaded = (src: string, key: string) => {
      const img = new Image();
      img.onload = () => handleImageLoad(key);
      img.onerror = () => handleImageLoad(key);
      img.src = src;
    };

    checkImageLoaded(window1Card1Back, 'step1-back');
    checkImageLoaded(window1Card1Front, 'step1-front');
  }, []);

  const steps = [
    {
      stepNumber: 1,
      title: 'How will you use ADUP?',
      subtitle: 'Choose your account type',
    },
  ];

  return (
    <div className='onboardingSidebar'>
      <div className='onboardingSidebar__header'>
        <img
          src={adupLogoSet}
          alt='TARA AdUp'
          className='onboardingSidebar__logo'
        />
      </div>

      <div className='onboardingSidebar__steps'>
        {steps.map((step) => {
          const stepCompleted = isStepCompleted(step.stepNumber);
          const isClickable = isStepAccessible(step.stepNumber);
          return (
            <SidebarCard
              key={step.stepNumber}
              stepNumber={step.stepNumber}
              title={step.title}
              subtitle={step.subtitle}
              isActive={currentStep === step.stepNumber}
              isCompleted={stepCompleted}
              isDisabled={!isClickable}
              onClick={() => {
                if (isClickable) {
                  onStepChange(step.stepNumber);
                }
              }}
            />
          );
        })}
      </div>

      <div className='onboardingSidebar__promo'>
        <div className='onboardingSidebar__promo-image'>
          <div className='onboardingSidebar__promo-layered'>
            <div className='onboardingSidebar__promo-layer onboardingSidebar__promo-layer--back'>
              <img
                src={window1Card1Back}
                alt='Automated reporting - Back'
                onLoad={() => handleImageLoad('step1-back')}
                onError={() => handleImageLoad('step1-back')}
              />
            </div>
            <div className='onboardingSidebar__promo-layer onboardingSidebar__promo-layer--front'>
              <img
                src={window1Card1Front}
                alt='Automated reporting - Front'
                onLoad={() => handleImageLoad('step1-front')}
                onError={() => handleImageLoad('step1-front')}
              />
            </div>
          </div>
        </div>
        <div className='onboardingSidebar__promo-content'>
          <h3 className='onboardingSidebar__promo-title'>{promoCard.title}</h3>
          <h4 className='onboardingSidebar__promo-subtitle'>
            {promoCard.subtitle}
          </h4>
          <p className='onboardingSidebar__promo-description'>
            {promoCard.description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingSidebar;
